<script setup>
import {onMounted, ref, watch} from 'vue'
import axios from 'axios'
import {useHead} from '@vueuse/head'
import {useRoute} from 'vue-router'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import EmailForm from '@/components/EmailForm.vue'
import VideoCard from '@/components/VideoCard.vue'
import {useI18n} from '@/i18n'

const route = useRoute()
const {t} = useI18n()
const detail = ref(null)
const related = ref([])
const loading = ref(true)

onMounted(async () => {
  const core = document.createElement('script')
  core.src = '/js/core.min.js'
  core.onload = () => {
    const template = document.createElement('script')
    template.src = '/js/script.js'
    document.body.appendChild(template)
  }
  document.body.appendChild(core)
  try {
    const response = await axios.get('/api/videos/', {params: {site: route.params.str}})
    detail.value = (response.data.results || response.data)[0] || null
    if (detail.value?.category) {
      const relatedResponse = await axios.get('/api/videos/', {params: {category: detail.value.category.site}})
      related.value = (relatedResponse.data.results || relatedResponse.data)
        .filter((video) => video.id !== detail.value.id).slice(0, 3)
    }
  } finally {
    loading.value = false
  }
})

watch(detail, (video) => {
  if (!video) return
  useHead({title: `${video.title} | TPA Housing`, meta: [{name: 'description', content: video.summary}]})
})
</script>

<template>
  <div class="page">
    <HomeHeader/>
    <main v-if="detail" class="video-detail-page">
      <div class="container">
        <nav class="video-breadcrumbs" aria-label="Breadcrumb">
          <a href="/">{{ t('common.home') }}</a><i class="mdi mdi-chevron-right"></i>
          <a href="/videos">{{ t('page.videos') }}</a><i class="mdi mdi-chevron-right"></i>
          <span>{{ detail.title }}</span>
        </nav>
        <div class="video-detail-heading">
          <div>
            <span v-if="detail.category" class="video-eyebrow">{{ detail.category.name }}</span>
            <h1>{{ detail.title }}</h1>
            <p>{{ detail.summary }}</p>
          </div>
          <a class="video-back" href="/videos"><i class="mdi mdi-arrow-left"></i>{{ t('common.backToVideos') }}</a>
        </div>
        <video class="video-player" :poster="detail.cover" controls playsinline preload="metadata">
          <source :src="detail.video_file" type="video/mp4">
        </video>
        <div class="video-detail-layout">
          <article class="video-copy" v-html="detail.description"></article>
          <aside class="video-contact-panel">
            <h3>{{ t('common.questions') }}</h3>
            <p>{{ t('video.contactText') }}</p>
            <EmailForm/>
          </aside>
        </div>
        <section v-if="related.length" class="related-videos">
          <div class="video-section-heading"><h2>{{ t('common.relatedVideos') }}</h2></div>
          <div class="video-grid video-grid-home">
            <VideoCard v-for="video in related" :key="video.id" :video="video"/>
          </div>
        </section>
      </div>
    </main>
    <div v-else-if="loading" class="video-state video-detail-state">{{ t('common.loading') }}</div>
    <div v-else class="video-state video-detail-state">{{ t('common.noVideos') }}</div>
    <Footer/>
  </div>
</template>
